import browser from '@openclassrooms/utils/browser';

const toSlug = (text: string) =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');

const createAnchor = (id: string, titleElement: HTMLElement) => {
  const anchor = browser.document.createElement('a');
  anchor.className = 'oc-title-anchor';
  anchor.href = `#${id}`;
  anchor.tabIndex = -1;
  anchor.innerHTML = '#';

  Object.assign(anchor.style, {
    position: 'absolute',
    left: '-1em',
    top: '0',
    textDecoration: 'none',
    opacity: '0',
    transition: 'opacity 0.2s',
    cursor: 'pointer',
    color: 'inherit',
    fontSize: '0.9em',
    userSelect: 'none',
    display: 'inline-block',
    zIndex: '1',
    width: '25px',
  } as Partial<CSSStyleDeclaration>);

  anchor.addEventListener('click', (e: MouseEvent) => {
    e.preventDefault();
    if (window.history && window.history.pushState) {
      window.history.pushState(null, '', `#${id}`);
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    } else {
      window.location.hash = `#${id}`;
    }
    titleElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  return anchor;
};

export const createAnchors = (domContainer: HTMLElement) => {
  const TITLE_SELECTORS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
  const titles = Array.from(
    domContainer.querySelectorAll(TITLE_SELECTORS.join(','))
  ) as HTMLElement[];

  titles.forEach((title) => {
    const id =
      title.getAttribute('data-claire-element-id') || title.id || toSlug(title.textContent || '');
    if (!id || title.querySelector('.oc-title-anchor')) return;

    if (getComputedStyle(title).position === 'static') {
      const existingInlineStyle = title.getAttribute('style') || '';
      const hasPositionInlineStyle = /(?:^|;)\s*position\s*:/.test(existingInlineStyle);
      if (!hasPositionInlineStyle) {
        const separator =
          existingInlineStyle && !existingInlineStyle.trim().endsWith(';') ? '; ' : '';
        title.setAttribute('style', `${existingInlineStyle}${separator}position: relative;`);
      }
    }

    if (!title.id) {
      title.setAttribute('id', id);
    }

    const anchor = createAnchor(id, title);
    title.addEventListener('mouseenter', () => {
      (anchor.style as any).opacity = '1';
    });
    title.addEventListener('mouseleave', () => {
      (anchor.style as any).opacity = '0';
    });
    title.insertBefore(anchor, title.firstChild);
  });
};

export const scrollToAnchor = (domContainer: HTMLElement) => {
  if (window.location.hash) {
    const hash = window.location.hash.replace('#', '');
    const target = domContainer.querySelector(
      `[data-claire-element-id="${hash.replace(/"/g, '"')}"]`
    ) as HTMLElement | null;
    if (target) {
      setTimeout(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
    }
  }
};

export default { createAnchors, scrollToAnchor };
